import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Upload, Loader2, FileText, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JobCard from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CITIES } from "@/data/categories";

export default function AiMatch() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ title: "", location: "", summary: "", skills: [] });
  const [skillInput, setSkillInput] = useState("");
  const [matches, setMatches] = useState(null);
  const [matching, setMatching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cvName, setCvName] = useState("");

  useEffect(() => {
    if (user?.profile) {
      setProfile({
        title: user.profile.title || "",
        location: user.profile.location || "",
        summary: user.profile.summary || "",
        skills: user.profile.skills || [],
      });
      setCvName(user.profile.cv_filename || "");
    }
  }, [user]);

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !profile.skills.includes(s)) {
      setProfile((p) => ({ ...p, skills: [...p.skills, s] }));
      setSkillInput("");
    }
  };

  const saveProfile = async () => {
    await api.put("/profile", profile);
    await refreshUser();
    toast.success("Profil kaydedildi");
  };

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCvName(data.filename);
      await refreshUser();
      toast.success("CV yüklendi", { description: data.text_extracted ? "CV metni AI için okundu." : "Dosya kaydedildi." });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const runMatch = async () => {
    await saveProfile();
    setMatching(true);
    setMatches(null);
    try {
      const { data } = await api.post("/ai/match");
      setMatches(data.matches);
      if (data.matches.length) toast.success(`${data.matches.length} uyumlu ilan bulundu!`);
      else toast.info("Uygun ilan bulunamadı, profilinizi zenginleştirin.");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setMatching(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="AI İş Eşleştirme | isbul.org" description="Yapay zeka ile CV'nizi iş ilanlarıyla eşleştirin, uyum skorunuzu görün." canonical="/ai-eslestirme" />
      <Header />

      <div className="bg-[#1f222d] text-white">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-4">
            <Sparkles size={15} className="text-blue-300" /> <span className="text-sm">Yapay Zeka Eşleştirme</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold">CV'ni analiz edelim, sana en uygun işleri bulalım</h1>
          <p className="text-slate-300 mt-3 max-w-2xl mx-auto">Profilini doldur veya CV yükle; yapay zeka işveren kriterleriyle karşılaştırıp uyum skorunu hesaplasın.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
        {!user ? (
          <div className="text-center bg-blue-50 border border-blue-100 rounded-2xl p-10">
            <p className="text-[#1f222d] font-semibold text-lg">AI eşleştirme için giriş yapın</p>
            <p className="text-slate-500 mt-1">Ücretsiz hesap oluşturarak yapay zeka eşleştirmeyi kullanabilirsiniz.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/giris"><Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">Giriş Yap</Button></Link>
              <Link to="/kayit"><Button variant="outline" className="rounded-full px-6">Kaydol</Button></Link>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            {/* Profile form */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 h-fit">
              <h2 className="font-bold text-lg text-[#1f222d] mb-4">Profiliniz</h2>

              <label className="text-sm font-medium text-[#1f222d]">Ünvan / Pozisyon</label>
              <input data-testid="ai-title" value={profile.title} onChange={(e) => setProfile((p) => ({ ...p, title: e.target.value }))} placeholder="Örn: Yazılım Geliştirici" className="w-full mt-1.5 mb-4 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />

              <label className="text-sm font-medium text-[#1f222d]">Şehir</label>
              <select data-testid="ai-location" value={profile.location} onChange={(e) => setProfile((p) => ({ ...p, location: e.target.value }))} className="w-full mt-1.5 mb-4 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                <option value="">Seçiniz</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>

              <label className="text-sm font-medium text-[#1f222d]">Yetenekler</label>
              <div className="flex gap-2 mt-1.5">
                <input data-testid="ai-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} placeholder="Yetenek ekle" className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />
                <button data-testid="ai-add-skill" onClick={addSkill} className="w-11 rounded-xl bg-blue-600 text-white flex items-center justify-center"><Plus size={18} /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {profile.skills.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-sm">
                    {s} <button onClick={() => setProfile((p) => ({ ...p, skills: p.skills.filter((x) => x !== s) }))}><X size={13} /></button>
                  </span>
                ))}
              </div>

              <label className="text-sm font-medium text-[#1f222d]">Özet / Deneyim</label>
              <textarea data-testid="ai-summary" value={profile.summary} onChange={(e) => setProfile((p) => ({ ...p, summary: e.target.value }))} rows={3} placeholder="Deneyimlerinizi kısaca anlatın" className="w-full mt-1.5 mb-4 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" />

              <label className="text-sm font-medium text-[#1f222d]">CV Yükle (PDF/DOCX)</label>
              <label data-testid="ai-cv-upload" className="mt-1.5 flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-xl px-4 py-3 cursor-pointer hover:border-blue-400 transition-colors">
                {uploading ? <Loader2 size={18} className="animate-spin text-blue-600" /> : cvName ? <FileText size={18} className="text-blue-600" /> : <Upload size={18} className="text-slate-400" />}
                <span className="text-sm text-slate-600 truncate">{uploading ? "Yükleniyor..." : cvName || "Dosya seç"}</span>
                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={uploadCv} />
              </label>

              <Button data-testid="ai-run-match" onClick={runMatch} disabled={matching} className="w-full mt-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 text-base font-semibold">
                {matching ? <><Loader2 size={18} className="animate-spin mr-2" /> Analiz ediliyor...</> : <><Sparkles size={18} className="mr-2" /> AI ile Eşleştir</>}
              </Button>
            </div>

            {/* Results */}
            <div>
              <h2 className="font-bold text-lg text-[#1f222d] mb-4">Eşleşme Sonuçları</h2>
              {matching && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-10 text-center">
                  <Loader2 size={32} className="animate-spin text-blue-600 mx-auto" />
                  <p className="text-[#1f222d] font-medium mt-4">Yapay zeka ilanları analiz ediyor...</p>
                </div>
              )}
              {matches === null && !matching && (
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-12 text-center">
                  <Sparkles size={36} className="text-blue-300 mx-auto mb-3" />
                  <p className="text-[#1f222d] font-medium">Profilinizi doldurup "AI ile Eşleştir"e basın</p>
                  <p className="text-sm text-slate-500 mt-1">En uygun ilanlar uyum skoruyla burada listelenir.</p>
                </div>
              )}
              {matches && matches.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  {matches.map((j) => <JobCard key={j.id} job={j} />)}
                </div>
              )}
              {matches && matches.length === 0 && (
                <p className="text-center text-slate-500 py-12">Uygun ilan bulunamadı.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
